import { ApiError } from './http';
import { secureFetchJson } from '../security/secureFetch';

export type IsoDateTime = string;
export type CheckVehicleStatus = 'ok' | 'not_found' | 'error';

export interface MntDate {
  raw: string | null;
  iso: string | null;
}

export interface FlexibleDate {
  raw: string | null;
  iso: string | null;
  iso_month: string | null;
  is_future_placeholder: boolean;
  is_past_placeholder: boolean;
}

export interface MntTimestamp {
  raw_ms: number | null;
  iso: string | null;
  date: string | null;
}

export interface Money {
  raw: string | null;
  amount: number | null;
  currency: 'EUR' | null;
}

export interface Co2 {
  raw: string | null;
  g_per_km: number | null;
}

export interface FuelConsumption {
  raw: string | null;
  l_per_100km: number | null;
}

export interface Noise {
  raw: string | null;
  db_a: number | null;
  rpm: number | null;
}

export type IconStatus = 'ok' | 'major_fault' | 'minor_fault' | 'info' | 'unknown';
export type Severity = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export interface MntResponse {
  schema_version: string;
  regnr: string | null;
  fetched_at: IsoDateTime;
  source_url: string | null;
  page_title: string | null;
  status: CheckVehicleStatus;
  errors: string[];
  vehicle: Vehicle;
  raw: {
    fields: { label: string; value: string; source: string }[];
    unmapped_tech_pairs: Record<string, string>;
  };
  _meta?: {
    strategy: string;
    browser: string;
    proxy: string | null;
    rung: number;
    attempts: number;
    trail: string[];
    served_at: IsoDateTime;
  };
}

export interface Vehicle {
  identification: Identification;
  summary: Summary;
  status_messages: StatusMessage[];
  restrictions: Restrictions;
  insurance: Insurance;
  mileage: Mileage;
  inspections: Inspections;
  technical_details: TechnicalDetails;
  operations_history: OperationsHistory;
}

export interface Identification {
  registration_number: string | null;
  requested_registration_number: string | null;
  vin: string | null;
  make_model: string | null;
  logo_image_path: string | null;
}

export interface Summary {
  first_registration: MntDate;
  category: string | null;
  body_type: string | null;
  body_color: string | null;
  engine_displacement_cc: number | null;
  engine_power_kw: number | null;
  fuel: string | null;
  transmission: string | null;
  drive_type: string | null;
  registration_certificate: string | null;
  registration_fee: {
    raw: string | null;
    status: 'paid' | 'payable_on_next_transfer' | 'unpaid' | 'unknown';
  };
  raw_pairs: Record<string, string | null>;
}

export interface StatusMessage {
  kind:
    | 'register_pledge'
    | 'inspection_valid'
    | 'inspection_expired'
    | 'insurance_active'
    | 'insurance_missing'
    | 'temporarily_deleted'
    | 'deleted'
    | 'theft'
    | 'other';
  severity: Severity;
  text: string | null;
  icon_class: string | null;
  type_class: string | null;
}

export interface Restrictions {
  section_present: boolean;
  register_pledge: {
    has_entries: boolean;
    entries: RegisterPledgeEntry[];
  };
}

export interface RegisterPledgeEntry {
  set_date: MntDate;
  pledgee: { raw: string | null; name: string | null; registry_code: string | null };
  priority: number | null;
  amount: Money;
  raw: Record<string, string | null>;
}

export interface Insurance {
  section_present: boolean;
  has_active_contract: boolean;
  entries: InsuranceEntry[];
}

export interface InsuranceEntry {
  status: IconStatus;
  icon_class: string | null;
  document_number: string | null;
  insurer: string | null;
  contract_type: string | null;
  valid_from: FlexibleDate;
  valid_until: FlexibleDate;
  raw: Record<string, string | null>;
}

export interface Mileage {
  section_present: boolean;
  chart: { parse_ok: boolean | null; parse_error: string | null; raw_script_length: number | null };
  point_count: number;
  summary: {
    earliest_km: number | null;
    latest_km: number | null;
    min_km: number | null;
    max_km: number | null;
    total_km_gained: number | null;
    total_span_days: number | null;
    total_span_years: number | null;
    avg_km_per_year: number | null;
    rollback_count: number;
    has_rollback: boolean;
    first_reported_at: MntTimestamp | null;
    last_reported_at: MntTimestamp | null;
  };
  timeseries: MileagePoint[];
  points: MileageAnalysisPoint[];
}

export interface MileagePoint {
  timestamp_ms: number | null;
  iso_date: string | null;
  iso_datetime: string | null;
  odometer_km: number | null;
}

export interface MileageAnalysisPoint {
  index: number;
  odometer_km: number | null;
  country_code: string | null;
  reported_at: MntTimestamp;
  delta_from_previous_km: number | null;
  days_since_previous: number | null;
  km_per_day_since_previous: number | null;
  km_per_year_since_previous: number | null;
  is_rollback: boolean;
}

export interface InspectionCategory {
  status: IconStatus;
  icon_class: string | null;
  faults: string[];
  tooltip: string | null;
}

export type InspectionCategoryKey =
  | 'pollution'
  | 'other_equipment'
  | 'body_frame'
  | 'suspension'
  | 'lights_electrical'
  | 'visibility'
  | 'steering'
  | 'brakes'
  | 'identification';

export interface InspectionEntry {
  inspection_date: MntDate;
  valid_until: FlexibleDate;
  station: string | null;
  type: {
    raw: string | null;
    code: 'regular' | 'repeat' | 'extraordinary' | 'initial' | 'other' | null;
  };
  decision: { status: IconStatus; passed: boolean | null; text: string | null; icon_class: string | null };
  categories: Record<InspectionCategoryKey, InspectionCategory>;
}

export interface Inspections {
  section_present: boolean;
  row_labels: string[];
  category_keys: string[];
  entry_count: number;
  latest_inspection_date: MntDate | null;
  current_valid_until: FlexibleDate | null;
  current_passed: boolean | null;
  entries: InspectionEntry[];
}

export interface Tire {
  raw: string | null;
  valid: boolean;
  width_mm: number | null;
  aspect_ratio_pct: number | null;
  construction: string | null;
  rim_diameter_in: number | null;
  reinforcement: string | null;
  load_index: number | null;
  load_index_secondary: number | null;
  speed_symbol: string | null;
  extras: string[];
  is_mud_snow: boolean;
  is_three_peak_mountain_snowflake: boolean;
  approx_max_load_kg: number | null;
  approx_max_load_secondary_kg: number | null;
  approx_max_speed_kmh: number | null;
  overall_diameter_mm: number | null;
}

export interface Axle {
  axle_name: string | null;
  position_index: number | null;
  tires: Tire[];
  tire_raw_strings: string[];
  tire_count: number;
  max_load_kg: number | null;
  registered_load_kg: number | null;
  steering_axle: boolean | null;
  driving_axle: boolean | null;
  raw: { Juhttelg: string | null; Veotelg: string | null };
}

export interface TechnicalDetails {
  section_present: boolean;
  registration: {
    first_registration: MntDate;
    estonia_registration: MntDate;
    country_of_origin: string | null;
    first_registration_context: 'new_in_estonia' | 'used_in_estonia' | 'in_estonia' | null;
    origin_raw: string | null;
  };
  classification: {
    category: string | null;
    category_code: string | null;
    body_type: string | null;
    body_color: string | null;
    multicolor: boolean | null;
  };
  engine: {
    model: string | null;
    displacement_cc: number | null;
    power_kw: number | null;
    fuel: string | null;
    fuel_combination: string | null;
    catalyst: boolean | null;
    transmission: string | null;
    drive_type: string | null;
    max_speed_kmh: number | null;
    speed_limit_kmh: number | null;
  };
  dimensions: {
    length_mm: number | null;
    width_mm: number | null;
    height_mm: number | null;
    track_mm: number | null;
  };
  masses: {
    gross_weight_kg: number | null;
    registered_mass_kg: number | null;
    kerb_weight_kg: number | null;
    load_capacity_kg: number | null;
    braked_trailer_max_kg: number | null;
    unbraked_trailer_max_kg: number | null;
    train_mass_kg: number | null;
    coupling_load_kg: number | null;
  };
  environment: {
    emission_standard: string | null;
    co2_nedc: Co2;
    co2_wltp: Co2;
    standing_noise: Noise;
    driving_noise: Noise;
  };
  axles_summary: {
    axle_count: number | null;
    wheel_count: number | null;
    wheelbase_mm: number | null;
    bogie_count: number | null;
  };
  other: {
    vin_location: string | null;
    manufacturer_plate_location: string | null;
    doors: number | null;
    seats: number | null;
    modification: string | null;
    type: string | null;
    variant: string | null;
    version: string | null;
    type_approval_number: string | null;
    type_approval_extension: string | null;
    body_code: string | null;
    notes: string | null;
  };
  conversion_permit: string | null;
  fuel_consumption: {
    city: FuelConsumption;
    highway: FuelConsumption;
    combined_nedc: FuelConsumption;
    combined_wltp: FuelConsumption;
  };
  axles: Axle[];
  tires: {
    allowed_spec_count: number;
    parsed_spec_count: number;
    unparsed_spec_count: number;
    allowed_specs: Tire[];
  };
  raw_pairs: Record<string, string>;
}

export type OperationCode =
  | 'owner_change_with_fee'
  | 'owner_change'
  | 'registration_certificate_not_returned'
  | 'registration_certificate_issued'
  | 'ordered_plate_issued'
  | 'plate_reissued'
  | 'plate_action'
  | 'plate_lost_or_stolen'
  | 'plate_returned'
  | 'plate_issued'
  | 'registered_in_estonia'
  | 'pre_registration_check'
  | 'temporary_deregistration_ended'
  | 'temporary_deregistration'
  | 'permanent_deregistration'
  | 'deregistration'
  | 'technical_inspection'
  | 'other'
  | 'unknown';

export interface OperationsHistory {
  section_present: boolean;
  entry_count: number;
  total_operation_count: number;
  has_more: boolean;
  code_summary: { code: OperationCode; count: number }[];
  entries: {
    date: MntDate;
    operation_count: number;
    operations: { raw: string | null; code: OperationCode }[];
  }[];
}

export class CheckVehicleError extends Error {
  readonly statusCode: number | null;
  readonly responseStatus: CheckVehicleStatus | null;

  constructor(message: string, statusCode: number | null = null, responseStatus: CheckVehicleStatus | null = null) {
    super(message);
    this.name = 'CheckVehicleError';
    this.statusCode = statusCode;
    this.responseStatus = responseStatus;
  }
}

export function sanitizeRegistration(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function validateRegistration(raw: string): string | null {
  const plate = sanitizeRegistration(raw);
  if (plate.length === 0) return 'Empty registration number';
  if (plate.length < 2 || plate.length > 8) return 'Registration number must be 2-8 characters';
  if (!/^[A-Z0-9]+$/.test(plate)) return 'Registration number must be alphanumeric';
  return null;
}

export function buildCheckVehicleUrl(plate: string): string {
  const configured = import.meta.env.VITE_CHECK_VEHICLE_API_URL?.trim();
  const endpoint =
    configured && configured.length > 0
      ? configured
      : import.meta.env.DEV
        ? '/fast/mnt'
        : 'https://details.autospy.ee/fast/mnt';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(endpoint, origin);
  url.searchParams.set('regnr', sanitizeRegistration(plate));
  const token = checkVehicleToken();
  if (token && token.length > 0) {
    url.searchParams.set('token', token);
  }
  return url.toString();
}

/** Interim /fast/mnt token, used only while the security layer is disabled server-side. */
export function checkVehicleToken(): string {
  return import.meta.env.VITE_CHECK_VEHICLE_API_TOKEN?.trim() ?? '';
}

export async function lookupVehicle(
  plate: string,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<MntResponse> {
  const validationError = validateRegistration(plate);
  if (validationError) throw new CheckVehicleError(validationError, 400, null);

  try {
    const data = await secureFetchJson<MntResponse>('/fast/mnt', {
      query: { regnr: sanitizeRegistration(plate) },
      signal: options.signal,
      timeoutMs: options.timeoutMs ?? 52_000,
      label: 'Vehicle lookup',
      legacyToken: checkVehicleToken(),
    });
    if (data.status === 'error') {
      throw new CheckVehicleError(data.errors.join('; ') || 'Vehicle portal returned an error', 200, data.status);
    }
    return data;
  } catch (err) {
    if (err instanceof CheckVehicleError) throw err;
    // secureFetchJson raises ApiError (incl. Quota/RateLimit subclasses); preserve the
    // status so the UI can branch, and let the reactive quota state drive the paywall.
    if (err instanceof ApiError) {
      const message =
        err.statusCode === 504 ? 'Vehicle lookup timed out. Try again in a moment.' : err.message;
      throw new CheckVehicleError(message, err.statusCode, null);
    }
    throw new CheckVehicleError(err instanceof Error ? err.message : 'Vehicle lookup failed', null, null);
  }
}
